import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';
import { getArticle } from '../lib/supabase';
import type { Article } from '../types/database.types';

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError('Article not found');
        setLoading(false);
        return;
      }

      try {
        const data = await getArticle(slug);
        setArticle(data);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Kevyn Franco`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt || article.title);
      }
    }
  }, [article]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'white',
        }}
      >
        <CircularProgress sx={{ color: '#dc143c' }} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'white',
          pt: 12,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ mb: 4, color: '#1a1a1a' }}>
            Article Not Found
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              color: '#dc143c',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#b01030',
              },
            }}
          >
            Back to Home
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'white',
        pt: 12,
        pb: 8,
      }}
    >
      <Container maxWidth="md">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            color: '#1a1a1a',
            fontWeight: 700,
            mb: 4,
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#dc143c',
            },
          }}
        >
          Back to Articles
        </Button>

        {/* Article Header */}
        <Box sx={{ mb: 6 }}>
          {article.image_url && (
            <Box
              component="img"
              src={article.image_url}
              alt={article.title}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '400px',
                objectFit: 'cover',
                borderRadius: '8px',
                mb: 4,
              }}
            />
          )}

          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: '#999',
              mb: 2,
            }}
          >
            {article.author} • {new Date(article.published_at || '').toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            {article.reading_time && ` • ${article.reading_time} min read`}
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              color: '#1a1a1a',
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            {article.title}
          </Typography>

          {article.excerpt && (
            <Typography
              sx={{
                fontSize: '1.25rem',
                color: '#666',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              {article.excerpt}
            </Typography>
          )}

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
              {article.tags.map((tag, index) => (
                <Box
                  key={index}
                  sx={{
                    px: 2,
                    py: 0.5,
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#666',
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Article Content */}
        <Box
          sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 900,
              color: '#1a1a1a',
              mt: 4,
              mb: 2,
            },
            '& h1': { fontSize: '2.5rem' },
            '& h2': { fontSize: '2rem' },
            '& h3': { fontSize: '1.5rem' },
            '& p': {
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: '#333',
              mb: 3,
            },
            '& a': {
              color: '#dc143c',
              textDecoration: 'underline',
              '&:hover': {
                color: '#b01030',
              },
            },
            '& ul, & ol': {
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: '#333',
              mb: 3,
              pl: 4,
            },
            '& li': {
              mb: 1,
            },
            '& code': {
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
              fontFamily: 'monospace',
            },
            '& pre': {
              backgroundColor: '#1a1a1a',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              mb: 3,
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
                color: 'white',
              },
            },
            '& blockquote': {
              borderLeft: '4px solid #dc143c',
              pl: 3,
              py: 1,
              my: 3,
              fontStyle: 'italic',
              color: '#666',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              my: 3,
            },
          }}
        >
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </Box>

        {/* Article Footer */}
        <Box
          sx={{
            mt: 8,
            pt: 4,
            borderTop: '2px solid #e0e0e0',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#999',
              mb: 2,
            }}
          >
            Views: {article.views_count || 0}
          </Typography>

          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              color: '#dc143c',
              fontWeight: 700,
              fontSize: '1.125rem',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#b01030',
              },
            }}
          >
            Back to All Articles
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ArticlePage;